import { useRef, type CSSProperties } from "react";
import { ArrowUpRight, CheckCircle2, CircleDot, ShieldAlert } from "lucide-react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { useMediaQuery } from "../hooks/useMediaQuery";
import type { Project } from "../content/portfolio";

type ProjectCardProps = {
  project: Project;
  index: number;
  totalCards: number;
};

function ProjectLink({ project }: { project: Project }) {
  return (
    <a
      className="project-detail-link"
      href={`/projects/${project.slug}`}
      aria-label={`查看${project.shortTitle}完整项目详情`}
    >
      <span>完整项目</span>
      <ArrowUpRight aria-hidden="true" />
    </a>
  );
}

function ProjectHeader({ project }: { project: Project }) {
  return (
    <header className="project-card-header">
      <span className="project-card-number" aria-hidden="true">{project.number}</span>
      <div className="project-card-meta">
        <span>{project.ownership}</span>
        <time>{project.period}</time>
      </div>
      <h3 id={`${project.slug}-title`}>{project.title}</h3>
      <ProjectLink project={project} />
    </header>
  );
}

function CompactProjectCard({ project }: { project: Project }) {
  return (
    <div className="project-card-shell project-card-shell--compact" data-testid="project-card">
      <article
        id={project.slug}
        aria-labelledby={`${project.slug}-title`}
        className="project-card-sticky project-card-sticky--compact"
      >
        <ProjectHeader project={project} />
        <div className="project-card-body project-card-body--compact">
          <div className="project-narrative project-narrative--compact">
            <p className="project-summary">{project.summary}</p>
            <p className="project-card-value">
              <span>交付价值</span>
              {project.value}
            </p>
          </div>

          <div className="project-evidence project-evidence--compact" id={`${project.slug}-evidence`}>
            <div className="project-console-heading">
              <span>KEY EVIDENCE / {project.number}</span>
              <span><i aria-hidden="true" /> 可核对</span>
            </div>
            <ul className="project-metrics project-metrics--compact" aria-label={`${project.shortTitle}可核对证据`}>
              {project.metrics.slice(0, 3).map((metric) => (
                <li key={metric}>
                  <CheckCircle2 aria-hidden="true" />
                  <span>{metric}</span>
                </li>
              ))}
            </ul>
            <p className="project-card-flow-summary">
              <span>执行链路</span>
              {project.flow.join(" → ")}
            </p>
            <p className="project-boundary">
              <ShieldAlert aria-hidden="true" />
              <span><strong>事实边界</strong>{project.boundary}</span>
            </p>
          </div>
        </div>
      </article>
    </div>
  );
}

function StackedProjectCard({ project, index, totalCards }: ProjectCardProps) {
  const shellRef = useRef<HTMLDivElement>(null);
  const reduceMotion = useReducedMotion();
  const targetScale = 1 - (totalCards - 1 - index) * 0.02;
  const { scrollYProgress } = useScroll({ target: shellRef, offset: ["start start", "end start"] });
  const animatedScale = useTransform(scrollYProgress, [0, 1], [1, targetScale]);
  const cardStyle = {
    scale: reduceMotion ? 1 : animatedScale,
    zIndex: index + 1,
    "--card-offset": `${index * 18}px`,
  } as CSSProperties;

  return (
    <div ref={shellRef} className="project-card-shell" data-testid="project-card">
      <motion.article
        id={project.slug}
        aria-labelledby={`${project.slug}-title`}
        className="project-card-sticky"
        style={cardStyle}
      >
        <ProjectHeader project={project} />

        <div className="project-card-body">
          <div className="project-narrative">
            <p className="project-summary">{project.summary}</p>
            <dl>
              <div>
                <dt>项目背景</dt>
                <dd>{project.background}</dd>
              </div>
              <div>
                <dt>我的职责</dt>
                <dd>{project.responsibility}</dd>
              </div>
              <div>
                <dt>关键难点</dt>
                <dd>{project.challenges}</dd>
              </div>
              <div>
                <dt>实际价值</dt>
                <dd>{project.value}</dd>
              </div>
            </dl>
          </div>

          <div className="project-evidence" id={`${project.slug}-evidence`}>
            <div className="project-console-heading">
              <span>RUN CONTEXT / {project.number}</span>
              <span><i aria-hidden="true" /> EVIDENCE READY</span>
            </div>
            <ol className="project-flow" aria-label={`${project.shortTitle}执行链路`}>
              {project.flow.map((step, stepIndex) => (
                <li key={step}>
                  <CircleDot aria-hidden="true" />
                  <span>{stepIndex + 1}</span>
                  <strong>{step}</strong>
                </li>
              ))}
            </ol>

            <div className="project-solution">
              <h4>解决方案</h4>
              <ul>
                {project.approach.map((item) => (
                  <li key={item}>
                    <CheckCircle2 aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="project-metrics">
              <h4>可核对证据</h4>
              <ul>
                {project.metrics.map((metric) => <li key={metric}>{metric}</li>)}
              </ul>
            </div>

            <ul className="project-stack" aria-label={`${project.shortTitle}技术栈`}>
              {project.stack.map((tool) => <li key={tool}>{tool}</li>)}
            </ul>

            <p className="project-boundary">
              <ShieldAlert aria-hidden="true" />
              <span><strong>事实边界</strong>{project.boundary}</span>
            </p>
          </div>
        </div>
      </motion.article>
    </div>
  );
}

export function ProjectCard(props: ProjectCardProps) {
  // Keep phone/tablet cards in normal document flow and avoid hidden scroll subscriptions.
  const compact = useMediaQuery("(max-width: 1099px)");
  return compact ? <CompactProjectCard project={props.project} /> : <StackedProjectCard {...props} />;
}
